// Typed Fastify API client
import { supabase, clearAuthSession, getAccessToken } from './supabase.js';
import { useAdminStore } from '../store/adminStore.js';
import type { EvidenceParentFields } from './evidence-target.js';
import type {
  Project, Estimate, Phase, Invoice, Dispute, Variation,
  Client, Payment, ComplianceItem, CostEntry, CreditNote, Comment,
  ComplianceBlocker, ComplianceTemplate, ComplianceTemplateItem, OverdueComplianceItem,
  PhaseApproval, PhaseSignoff, RetentionRelease,
  AuditLogEntry,
  CreateProjectBody, UpdateProjectBody, CreateEstimateBody, UpdateEstimateBody, CreateDisputeBody,
  CreateVariationBody, MarkPaidBody, VerificationRole, TeamRole,
  EstimateCostSummary, CostType, ClientLifecycleStatus, ProjectFinancialPosition,
  VariationCostSummary,
  Opportunity, OpportunityStage, OpportunityStageSummary,
  CreateOpportunityBody, UpdateOpportunityBody, MoveOpportunityStageBody,
  SiteVisit,
  SiteVisitAttachment,
  SiteVisitEstimateBrief,
  SiteVisitMeasurement,
  SiteVisitRisk,
  SiteVisitStatus,
  RiskSeverity,
  ProjectUpdate,
  ProjectUpdateDraftResult,
  ProjectUpdateSaveBody,
} from '@buildc/shared';
import type { PhaseTask } from '@buildc/shared';
import type {
  TaskCreateBody, TaskUpdateBody, ScopeLockRead, EvidenceRecord, AdminTenantDetail, TenantSettings, TenantSettingsUpdate,
} from './api-types.js';
import type { StripeAccountStatus } from '@buildc/shared';

export type {
  TaskCreateBody, TaskUpdateBody, ScopeLockRead, EvidenceRecord, AdminTenantDetail, TenantSettings, TenantSettingsUpdate,
} from './api-types.js';

const BASE_URL = '/api';

/**
 * Builds a query string, dropping keys that have no value.
 *
 * `new URLSearchParams({ phase_id: undefined })` keeps the key and serialises it
 * as the literal text `phase_id=undefined`, which the API then hands to Postgres
 * as a uuid. The request 500s and the caller renders an empty list, so the
 * failure looks like "no results" rather than an error.
 */
export function toQueryString(params?: Record<string, string | undefined | null>): string {
  if (!params) return '';
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') out.append(k, v);
  }
  return out.toString();
}

/** What a customer gives to accept a quote: their own full name, and an explicit yes. */
export interface AcceptanceSignature {
  accepted_name: string;
  accept_confirmed: true;
}

export class ApiError extends Error {
  /**
   * `code` carries the INV-xxx identifier when the API refused on a business rule. The
   * message is written for a person; the code is what calling code can branch on without
   * matching prose — the offline queue uses it to tell a permanent refusal from a blip.
   */
  constructor(public statusCode: number, message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * True when a request failed because there is no connection, rather than because
 * the request itself was rejected.
 *
 * A builder on site loses signal constantly. The service worker answers a cache
 * miss with 503, an offline 401 is a JWT that could not be reached rather than a
 * rejected session, and fetch itself rejects with a TypeError when the network is
 * gone. None of those mean anything is wrong with the app, so none of them should
 * surface as an error panel — whatever is already on screen is still the best
 * data available.
 *
 * Lives here, next to the two throws it describes, so a change to either one is
 * made with this in view.
 */
export function isOfflineError(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.statusCode === 503) return true;
    return error.statusCode === 401 && !navigator.onLine;
  }
  return error instanceof TypeError;
}

/**
 * Every authenticated fetch — JSON or blob — goes through here, so the 401
 * refresh-and-retry only has to be right once. A hand-rolled fetch that skips this
 * (as the PDF viewer used to) throws on a merely-expired token instead of recovering
 * the way every other request does.
 */
export async function fetchWithAuthRetry(
  path: string,
  init: RequestInit = {},
  isRetry = false
): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new ApiError(401, 'Not authenticated');

  const { activeTenantId } = useAdminStore.getState();
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
    'Authorization': `Bearer ${token}`,
  };
  if (activeTenantId) headers['X-Tenant-ID'] = activeTenantId;

  const res = await fetch(`${BASE_URL}${path}`, { ...init, headers });

  // If the device is offline the JWT cannot be verified server-side — do not attempt
  // a refresh, and let the caller decide whether to clear the session.
  if (res.status === 401 && !isRetry && navigator.onLine) {
    const { data: refreshed } = await supabase.auth.refreshSession();
    if (refreshed.session) {
      return fetchWithAuthRetry(path, init, true);
    }
  }

  return res;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal
): Promise<T> {
  const res = await fetchWithAuthRetry(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });

  if (!res.ok) {
    // Treat a 503 from the service worker as an offline error — do not clear session
    if (res.status === 503) {
      throw new ApiError(503, 'You appear to be offline. Showing cached data.');
    }

    const err = await res.json().catch(() => ({ message: res.statusText }));

    if (res.status === 401) {
      // If the device is offline the JWT cannot be verified server-side — do not clear
      // the session. The builder retains their cached data until connectivity returns.
      if (!navigator.onLine) {
        throw new ApiError(401, 'Offline — session preserved');
      }
      await clearAuthSession();
    }

    throw new ApiError(res.status, err.message ?? 'Request failed', err.code);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// Signup and login run with no session, so they cannot go through request() —
// it requires an access token and would reject them as 'Not authenticated'
// before the fetch is made. The 401 branch is omitted deliberately: refreshing
// and clearing a session that does not exist would sign the visitor out mid-signup.
async function publicRequest<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new ApiError(res.status, err.message ?? 'Request failed', err.code);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// Exported for the feature clients split out of this file to keep it under the size limit
// (cost-control-api.ts). They share this one request path, so auth retry and ApiError apply.
export const get = <T>(path: string, signal?: AbortSignal) => request<T>('GET', path, undefined, signal);
export const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body);
export const patch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, body);
export const put = <T>(path: string, body?: unknown) => request<T>('PUT', path, body);
export const del = (path: string) => request<void>('DELETE', path);

export const api = {
  // Auth
  auth: {
    signup: (body: { email: string; password: string; full_name: string; company_name: string }) =>
      publicRequest('POST', '/auth/signup', body),
    login: (body: { email: string; password: string }) =>
      publicRequest<{ access_token: string; refresh_token: string; user: unknown }>('POST', '/auth/login', body),
      forgotPassword: (body: {email: string }) =>
        publicRequest<{ message: string}>('POST', '/auth/forgot-password', body),
      resetPassword: (body: {token: string; password: string }) =>
        publicRequest<{ email: string }>('POST', '/auth/reset-password', body),
    // A portal link's token becomes a session (BC-169). No password is involved.
    portalSession: (token: string) =>
      publicRequest<{ access_token: string; refresh_token: string; expires_at?: number; target_path: string }>(
        'POST', '/auth/portal-session', { token }
      ),
    resendPortalLink: (email: string) =>
      publicRequest<{ message: string }>('POST', '/auth/portal-link/resend', { email }),
    },

  // Opportunities — the enquiry pipeline
  opportunities: {
    // Stage is repeatable, so it is built by hand rather than through toQueryString: the
    // pipeline asks for several at once and `stage=new&stage=contacted` is what the API reads.
    list: (params?: { stage?: OpportunityStage[]; client_id?: string; source?: string; overdue_action?: boolean }) => {
      const qs = new URLSearchParams();
      for (const stage of params?.stage ?? []) qs.append('stage', stage);
      if (params?.client_id) qs.set('client_id', params.client_id);
      if (params?.source) qs.set('source', params.source);
      if (params?.overdue_action) qs.set('overdue_action', 'true');
      const q = qs.toString();
      return get<{ data: Opportunity[]; summary: OpportunityStageSummary[] }>(`/opportunities${q ? `?${q}` : ''}`);
    },
    get: (id: string) => get<Opportunity>(`/opportunities/${id}`),
    create: (body: CreateOpportunityBody) => post<Opportunity>('/opportunities', body),
    update: (id: string, body: UpdateOpportunityBody) => patch<Opportunity>(`/opportunities/${id}`, body),
    // The only manual stage writer. Estimate and Quote are not settable here — the platform
    // moves those when the quote is raised and sent.
    setStage: (id: string, body: MoveOpportunityStageBody) =>
      post<Opportunity>(`/opportunities/${id}/stage`, body),
    // Gives the enquiry a project so it can be quoted. Does not move the stage: raising the
    // quote is what does that.
    convert: (id: string) => post<Opportunity>(`/opportunities/${id}/convert`),
    remove: (id: string) => del(`/opportunities/${id}`),
  },

  // Site visits — the survey behind the quote. Builder-only on every handler: risks,
  // mitigations and access constraints are the builder's own assessment of a customer's
  // property.
  siteVisits: {
    list: (params?: {
      status?: SiteVisitStatus[];
      assigned_to?: string;
      from?: string;
      to?: string;
      opportunity_id?: string;
      /**
       * Joined through the enquiry on the API side. This is how a project reaches its survey:
       * both screens that need it — the project page and the quote brief — are opened with a
       * project id and no enquiry id.
       */
      project_id?: string;
    }) => {
      const qs = new URLSearchParams();
      for (const status of params?.status ?? []) qs.append('status', status);
      for (const key of ['assigned_to', 'from', 'to', 'opportunity_id', 'project_id'] as const) {
        const value = params?.[key];
        if (value) qs.set(key, value);
      }
      const q = qs.toString();
      return get<{ data: SiteVisit[] }>(`/site-visits${q ? `?${q}` : ''}`);
    },
    get: (id: string) => get<SiteVisit>(`/site-visits/${id}`),
    create: (body: {
      opportunity_id: string;
      scheduled_at: string;
      site_address?: string;
      duration_minutes?: number;
      assigned_to?: string;
      notes?: string;
      access_notes?: string;
    }) => post<SiteVisit>('/site-visits', body),
    update: (id: string, body: Record<string, unknown>) => patch<SiteVisit>(`/site-visits/${id}`, body),
    complete: (id: string, body: { notes?: string; follow_up?: string; follow_up_due?: string }) =>
      post<SiteVisit>(`/site-visits/${id}/complete`, body),
    cancel: (id: string, body: { reason: string }) => post<SiteVisit>(`/site-visits/${id}/cancel`, body),
    remove: (id: string) => del(`/site-visits/${id}`),
    /** The survey shaped as the quote brief. Prefills the form; never writes it. */
    estimateBrief: (id: string) => get<SiteVisitEstimateBrief>(`/site-visits/${id}/estimate-brief`),
    measurements: {
      add: (visitId: string, body: { label: string; value: number; unit: string; note?: string; sort_order?: number }) =>
        post<SiteVisitMeasurement>(`/site-visits/${visitId}/measurements`, body),
      update: (measurementId: string, body: Record<string, unknown>) =>
        patch<SiteVisitMeasurement>(`/site-visits/measurements/${measurementId}`, body),
      remove: (measurementId: string) => del(`/site-visits/measurements/${measurementId}`),
    },
    risks: {
      add: (visitId: string, body: { description: string; severity: RiskSeverity; mitigation?: string; affects_price?: boolean }) =>
        post<SiteVisitRisk>(`/site-visits/${visitId}/risks`, body),
      update: (riskId: string, body: Record<string, unknown>) =>
        patch<SiteVisitRisk>(`/site-visits/risks/${riskId}`, body),
      remove: (riskId: string) => del(`/site-visits/risks/${riskId}`),
    },
    attachments: {
      presign: (visitId: string, body: { filename: string; mime_type: string; file_size: number }) =>
        post<{ upload_url: string; s3_key: string }>(`/site-visits/${visitId}/attachments/presign`, body),
      record: (visitId: string, body: Record<string, unknown>) =>
        post<SiteVisitAttachment>(`/site-visits/${visitId}/attachments`, body),
      // Takes the visit id although the path does not: the caller always holds it, and a
      // delete that cannot say which visit it belongs to is one the grid cannot route.
      remove: (_visitId: string, attachmentId: string) => del(`/site-visits/attachments/${attachmentId}`),
    },
  },

  // Projects
  projects: {
    list: () => get<{ data: Project[] }>('/projects'),
    get: (id: string) => get<Project>(`/projects/${id}`),
    create: (body: CreateProjectBody) => post<Project>('/projects', body),
    update: (id: string, body: UpdateProjectBody) => patch<Project>(`/projects/${id}`, body),
    complete: (id: string) => post<Project>(`/projects/${id}/complete`),
    forecast: (id: string) => get<ProjectFinancialPosition>(`/projects/${id}/forecast`),
    reopen: (id: string, body: { reason: string }) => post<Project>(`/projects/${id}/reopen`, body),
    // Required before a plan with any inspector-signed phase can activate (INV-014).
    assignInspector: (id: string, inspectorUserId: string) =>
      patch<Project>(`/projects/${id}`, { inspector_user_id: inspectorUserId }),
    // The API refuses this (400) while a phase still needs the inspector's signature —
    // the caller surfaces that message rather than assuming the clear always succeeds.
    clearInspector: (id: string) => patch<Project>(`/projects/${id}`, { inspector_user_id: null }),
    delete: (id: string) => del(`/projects/${id}`),
  },

  // Clients
  clients: {
    // The filter is named field by field rather than forwarded as an object: passed
    // straight to useQuery (`useQuery('clients', api.clients.list)`) this receives
    // react-query's QueryFunctionContext as its argument, and spreading that into the
    // query string would serialise `queryKey`, `signal` and `meta` into the URL. Naming
    // the one field it accepts makes that harmless instead of invisible.
    list: (params?: { lifecycle_status?: ClientLifecycleStatus }) => {
      const qs = toQueryString({ lifecycle_status: params?.lifecycle_status });
      return get<{ data: Client[] }>(`/clients${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<Client>(`/clients/${id}`),
    // A dedicated endpoint, not a field on update() — the lifecycle move is audited and
    // the transition is validated against where the customer stands now, neither of which
    // PATCH /clients/:id does.
    setLifecycle: (id: string, body: { lifecycle_status: ClientLifecycleStatus; reason?: string }) =>
      patch<Client>(`/clients/${id}/lifecycle`, body),
    create: (body: { full_name: string; email: string; phone?: string; address?: string; client_type?: 'business' | 'domestic' }) =>
      post<Client>('/clients', body),
    update: (id: string, body: { full_name?: string; email?: string; phone?: string; address?: string; client_type?: 'business' | 'domestic' }) =>
      patch<Client>(`/clients/${id}`, body),
    delete: (id: string) => del(`/clients/${id}`),
    invitePortal: (id: string) =>
      post<{ message: string; user_id: string; email: string; sent_to: string; tenant_user_id: string }>(`/clients/${id}/invite-portal`),
    sendPortalLink: (id: string) =>
      post<{ id: string; expires_at: string; sent_to: string }>(`/clients/${id}/portal-link`),
  },

  // Team
  team: {
    // user_id is null until an invited member signs up, and changeRole/remove are keyed
    // on it — so the null is what tells the caller those two are unavailable for a row.
    list: () => get<{ data: { id: string; user_id: string | null; email: string; full_name: string; role: string; created_at: string; is_pending: boolean }[] }>('/team'),
    get: (userId: string) =>
      get<{
        id: string; email: string; full_name: string; avatar_url: string | null;
        user_id: string; role: string; created_at: string; is_pending: boolean;
        projects_raised: number; phases_signed_off: number; payments_recorded: number;
        quotes_raised: number; evidence_uploaded: number;
        // Absent unless the caller can read the audit trail (builder_admin/superadmin).
        audit_entry_count?: number;
        projects: { id: string; title: string; status: string; created_at: string }[];
        quotes: { id: string; project_title: string; version: number; status: string;
          total: number; created_at: string }[];
        signoffs: { id: string; phase_title: string; project_title: string;
          signed_role: string; created_at: string }[];
        evidence: { id: string; phase_title: string; project_title: string;
          filename: string; created_at: string }[];
        payments: { id: string; amount: number; received_at: string; invoice_number: string }[];
        audit_entries?: { id: string; event_type: string; entity_type: string; entity_id: string; created_at: string }[];
      }>(`/team/${userId}`),
    invite: (body: { email: string; full_name: string; role: TeamRole }) =>
      post('/team', body),
    changeRole: (userId: string, role: TeamRole) =>
      patch(`/team/${userId}`, { role }),
    remove: (userId: string) => del(`/team/${userId}`),
  },

  // Estimates
  estimates: {
    list: (projectId?: string, clientId?: string) => {
      const qs = toQueryString({ project_id: projectId, client_id: clientId });
      return get<{ data: Estimate[] }>(`/estimates${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<Estimate>(`/estimates/${id}`),
    // Cost against sell — direct cost, the tenant's uplift, net total, GP and margin, plus
    // the same figures per cost type and per work package. A separate read from the quote
    // itself, so nothing client-facing ever assembles cost; builder_admin only.
    costSummary: (id: string) => get<EstimateCostSummary>(`/estimates/${id}/cost-summary`),
    create: (body: CreateEstimateBody) => post<Estimate>('/estimates', body),
    // A draft is refined in place — no new version. Refused once the quote has been sent.
    update: (id: string, body: UpdateEstimateBody) => patch<Estimate>(`/estimates/${id}`, body),
    send: (id: string) => post<Estimate>(`/estimates/${id}/send`),
    // Past draft a quote is superseded, not edited: this copies it to the next version and
    // leaves the original exactly as the client was sent it.
    revise: (id: string) => post<Estimate>(`/estimates/${id}/revise`),
    // A draft nobody has been shown — most often the copy `revise` just made by mistake.
    // Refused once it has been sent; there is no undo for a figure a client has seen.
    discard: (id: string) => del(`/estimates/${id}`),
    // The customer's typed name and confirmation are the signature on the acceptance (BC-169).
    accept: (estimateId: string, projectId: string, signature: AcceptanceSignature) =>
      post('/scope-locks', { estimate_id: estimateId, project_id: projectId, ...signature }),
    reject: (id: string, body: { reason?: string } = {}) =>
      post<Estimate>(`/estimates/${id}/reject`, body),
    // Negotiating, not declining: the quote stays open and can still be accepted.
    // At least one of the two must carry something — the API refuses an empty request.
    requestChanges: (
      id: string,
      body: { overall_comment?: string; line_comments?: { line_item_id: string; comment: string }[] }
    ) => post<Estimate>(`/estimates/${id}/request-changes`, body),
    pdfUrl: (id: string) => `${BASE_URL}/pdf/estimate/${id}`,
  },

  // AI Scope rate card
  rateCard: {
    list: () => get<{ data: unknown[] }>('/rate-card'),
    get: (id: string) => get<unknown>(`/rate-card/${id}`),
    // unit_cost + cost_type are what make a costed quote line one tap instead of three
    // typed fields — a cost that has to be entered by hand on every line stays empty.
    create: (body: {
      description: string; unit: string; unit_rate: number; category: string;
      unit_cost?: number | null; cost_type?: CostType | null;
    }) => post('/rate-card', body),
    update: (id: string, body: {
      unit_rate?: number; description?: string;
      unit_cost?: number | null; cost_type?: CostType | null;
    }) => patch(`/rate-card/${id}`, body),
    delete: (id: string) => del(`/rate-card/${id}`),
    seed: () => post('/rate-card/seed'),
  },

  // Phases
  phases: {
    list: (projectId?: string) => get<{ data: Phase[] }>(`/phases${projectId ? `?project_id=${projectId}` : ''}`),
    get: (id: string) => get<Phase>(`/phases/${id}`),
    create: (body: { project_id: string; title: string; description?: string; contracted_value: number; sort_order?: number; verification_role?: VerificationRole }) =>
      post<Phase>('/phases', body),
    // verification_role is refused once the plan is active — see the phase editor, which
    // only offers it while the plan is still in draft.
    update: (id: string, body: { title?: string; description?: string; contracted_value?: number; sort_order?: number; verification_role?: VerificationRole; planned_start_date?: string | null; planned_end_date?: string | null }) =>
      patch<Phase>(`/phases/${id}`, body),
    delete: (id: string) => del(`/phases/${id}`),
    start: (id: string) => post<Phase>(`/phases/${id}/start`),
    submit: (id: string) => post<Phase>(`/phases/${id}/submit`),
    // No body approves in full — the server derives the split, so existing callers
    // keep the behaviour they had before partial approval existed.
    approve: (id: string, body?: { approved_amount?: number; retention_amount?: number }) =>
      post<Phase>(`/phases/${id}/approve`, body),
    // Sends the phase back for a correction without raising a dispute — no payment
    // freeze, no dispute record. The comment is required; the server refuses under 10
    // characters, because a fix request with nothing in it leaves the builder guessing.
    requestFixes: (id: string, body: { comment: string }) =>
      post<Phase>(`/phases/${id}/request-fixes`, body),
    releaseRetention: (id: string, body: { amount: number; note?: string }) =>
      post<Phase>(`/phases/${id}/release-retention`, body),
    activatePlan: (projectId: string) => post(`/phases/plan/${projectId}/activate`),
    getPlan: (projectId: string) => get<{ id?: string; is_active: boolean; activated_at?: string }>(`/phases/plan/${projectId}`),
    approval: (id: string) => get<{ data: PhaseApproval | null }>(`/phases/${id}/approval`),
    signoffs: (id: string) => get<{ data: PhaseSignoff[] }>(`/phases/${id}/signoffs`),
    retentionReleases: (id: string) => get<{ data: RetentionRelease[] }>(`/phases/${id}/retention-releases`),
  },

  // Delivery tasks under a phase — builder_admin/builder_staff only. Status is set, never
  // toggled, so a retried request lands on the same value.
  tasks: {
    list: (scope: { phase_id: string } | { project_id: string }) =>
      get<{ data: PhaseTask[] }>(`/tasks?${toQueryString(scope)}`),
    create: (body: TaskCreateBody) => post<PhaseTask>('/tasks', body),
    update: (id: string, body: TaskUpdateBody) => patch<PhaseTask>(`/tasks/${id}`, body),
    delete: (id: string) => del(`/tasks/${id}`),
    reorder: (phaseId: string, ids: string[]) =>
      post<{ data: PhaseTask[] }>('/tasks/reorder', { phase_id: phaseId, ids }),
  },

  // Scope locks
  scopeLocks: {
    get: (projectId: string) => get<ScopeLockRead>(`/scope-locks/${projectId}`),
    accept: (body: { estimate_id: string; project_id: string } & AcceptanceSignature) => post('/scope-locks', body),
  },

  // Evidence
  evidence: {
    list: (phaseId: string) => get<{ data: unknown[] }>(`/evidence?phase_id=${phaseId}`),
    get: (id: string) => get<EvidenceRecord>(`/evidence/${id}`),
    /** The evidence filed against any one parent — a change order, a certificate, a dispute. */
    listFor: (parent: EvidenceParentFields) =>
      get<{ data: unknown[] }>(`/evidence?${new URLSearchParams(parent as Record<string, string>)}`),
    presign: (body: EvidenceParentFields & { filename: string; mime_type: string; file_size: number }) =>
      post<{ upload_url: string; s3_key: string }>('/evidence/presign', body),
    record: (body: EvidenceParentFields & {
      s3_key: string; filename: string; mime_type: string;
      file_size: number; caption?: string; gps_lat?: number; gps_lng?: number; taken_at?: string;
      round_token?: string;
    }) => post('/evidence', body),
    delete: (id: string) => del(`/evidence/${id}`),
  },

  // Invoices
  invoices: {
    list: (params?: { project_id?: string; phase_id?: string; status?: string; client_id?: string }) => {
      const qs = toQueryString(params);
      return get<{ data: Invoice[] }>(`/invoices${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<Invoice>(`/invoices/${id}`),
    send: (id: string) => post<Invoice>(`/invoices/${id}/send`),
    markPaid: (id: string, body: MarkPaidBody) => post<{ message: string }>(`/invoices/${id}/mark-paid`, body),
    checkout: (id: string) => post<{ url: string }>(`/invoices/${id}/checkout`),
    flagError: (id: string, reason: string) => post(`/invoices/${id}/flag-error`, { reason }),
    remind: (id: string) => post<{ message: string }>(`/invoices/${id}/remind`),
    addLatePaymentInterest: (id: string) =>
      post<{ id: string; invoice_number: string; interest: number; compensation: number }>(
        `/invoices/${id}/add-late-payment-interest`
      ),
    pdfUrl: (id: string) => `${BASE_URL}/pdf/invoice/${id}`,
    openPdf: async (id: string, tab: Window | null) => {
      const res = await fetchWithAuthRetry(`/pdf/invoice/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: 'Failed to load PDF' }));
        throw new ApiError(res.status, body.message ?? 'Failed to load PDF', body.code);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);

      // Callers pre-open the tab inside the click handler because this window.open
      // runs after two awaits, by which point user activation has lapsed and a popup
      // blocker refuses it. If the pre-opened tab was blocked too there is nowhere to
      // put the PDF — say so, or the user clicks PDF and nothing happens at all.
      const target = tab ?? window.open(url, '_blank');
      if (!target) {
        URL.revokeObjectURL(url);
        throw new ApiError(0, 'Could not open the PDF — allow pop-ups for this site and try again');
      }
      if (tab) tab.location.href = url;

      // The tab needs the URL to still resolve once it finishes loading the PDF, so
      // revoking must wait rather than happen as soon as this function returns.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  },

  // Credit notes
  creditNotes: {
    list: (invoiceId?: string) => get<{ data: CreditNote[] }>(`/credit-notes${invoiceId ? `?invoice_id=${invoiceId}` : ''}`),
    get: (id: string) => get<CreditNote>(`/credit-notes/${id}`),
    create: (body: { original_invoice_id: string; reason: string; amount: number; vat_amount?: number }) =>
      post<CreditNote>('/credit-notes', body),
    issue: (id: string, revisedInvoiceId?: string) =>
      post<CreditNote>(`/credit-notes/${id}/issue`, { revised_invoice_id: revisedInvoiceId }),
    pdfUrl: (id: string) => `${BASE_URL}/pdf/credit-note/${id}`,
    /**
     * Fetches the PDF with the session's bearer token and hands the blob to a tab the
     * caller pre-opened. Navigating straight to `pdfUrl` cannot work: /api/pdf is not a
     * public path, and a plain window.open sends no Authorization header, so the builder
     * gets a tab containing a 401 body. Same shape as invoices.openPdf — see the note
     * there about pre-opening the tab inside the click handler.
     */
    openPdf: async (id: string, tab: Window | null) => {
      const res = await fetchWithAuthRetry(`/pdf/credit-note/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({ message: 'Failed to load PDF' }));
        throw new ApiError(res.status, body.message ?? 'Failed to load PDF', body.code);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const target = tab ?? window.open(url, '_blank');
      if (!target) {
        URL.revokeObjectURL(url);
        throw new ApiError(0, 'Could not open the PDF — allow pop-ups for this site and try again');
      }
      if (tab) tab.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  },

  // Payments
  payments: {
    list: (params?: { project_id?: string; invoice_id?: string; client_id?: string }) => {
      const qs = toQueryString(params);
      return get<{ data: Payment[] }>(`/payments${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<{ data: Payment }>(`/payments/${id}`),
  },

  // Audit trail — builder-only, and the only read path to it. `payload` is not returned;
  // see the route's own note on why the trail answers who and when, not what was in it.
  audit: {
    // The entity-scoped trail embedded on a record's own Related tab.
    list: (entityType: string, entityId: string) =>
      get<{ data: AuditLogEntry[] }>(`/audit-log?entity_type=${entityType}&entity_id=${entityId}`),
    // The tenant-wide, filterable list — /builder/audit. builder_admin/superadmin only.
    // Paged: pass the previous page's next_cursor as `cursor`; null means that was the last.
    listAll: (filters: Record<string, string>) =>
      get<{ data: AuditLogEntry[]; next_cursor: string | null }>(`/audit?${new URLSearchParams(filters)}`),
    get: (id: string) => get<AuditLogEntry>(`/audit/${id}`),
  },

  // Disputes
  disputes: {
    list: (params?: { project_id?: string; phase_id?: string; client_id?: string } | string) => {
      const qs = typeof params === 'string'
        ? `project_id=${params}`
        : toQueryString(params);
      return get<{ data: Dispute[] }>(`/disputes${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<Dispute>(`/disputes/${id}`),
    create: (body: CreateDisputeBody) => post<Dispute>('/disputes', body),
    respond: (id: string, body: { response: 'accept' | 'contest'; explanation: string; evidence_s3_keys?: string[] }) =>
      post<Dispute>(`/disputes/${id}/respond`, body),
    // The client's escalation from a builder response to a neutral review. The comment is
    // required — the server refuses under 10 characters, same bound as request-fixes.
    requestReview: (id: string, body: { comment: string }) =>
      post<Dispute>(`/disputes/${id}/request-review`, body),
    resolve: (id: string, body: { resolution: string; approved_pct?: number; notes?: string }) =>
      post<Dispute>(`/disputes/${id}/resolve`, body),
  },

  // Variations
  variations: {
    list: (projectId?: string) => get<{ data: Variation[] }>(`/variations${projectId ? `?project_id=${projectId}` : ''}`),
    get: (id: string) => get<Variation>(`/variations/${id}`),
    create: (body: CreateVariationBody) => post<Variation>('/variations', body),
    update: (id: string, body: Omit<CreateVariationBody, 'project_id'>) => patch<Variation>(`/variations/${id}`, body),
    send: (id: string) => post<Variation>(`/variations/${id}/send`),
    approve: (id: string) => post<Variation>(`/variations/${id}/approve`),
    reject: (id: string) => post<Variation>(`/variations/${id}/reject`),
    correct: (id: string) => post<Variation>(`/variations/${id}/correct`),
    approvalLink: (id: string) => post<{ url: string; expires_at: string }>(`/variations/${id}/approval-link`),
    // builder_admin/superadmin only — the caller must not issue it for anyone else.
    costSummary: (projectId: string) =>
      get<VariationCostSummary>(`/variations/cost-summary?project_id=${projectId}`),
  },

  // Compliance
  compliance: {
    list: (projectId: string) => get<{ data: ComplianceItem[] }>(`/compliance?project_id=${projectId}`),
    get: (id: string) => get<ComplianceItem>(`/compliance/${id}`),
    /**
     * Compliance evidence has its own presign. The checklist used to borrow the phase
     * one and pass a project id as `phase_id`, which matched no phase and 404'd — and
     * because that throw happened before `complete()` was called, attaching a
     * certificate blocked the completion entirely.
     */
    presign: (id: string, body: { filename: string; mime_type: string; file_size: number }) =>
      post<{ upload_url: string; s3_key: string }>(`/compliance/${id}/presign`, body),
    create: (body: { project_id: string; title: string; description?: string; is_mandatory?: boolean; category?: string }) =>
      post<ComplianceItem>('/compliance', body),
    // The file details travel with the key so the certificate's evidence row carries its real
    // name, type and size rather than what can be guessed from the bucket path.
    complete: (
      id: string,
      certificate?: { s3_key: string; filename: string; mime_type: string; file_size: number }
    ) =>
      post<ComplianceItem>(`/compliance/${id}/complete`, certificate
        ? {
            evidence_s3_key: certificate.s3_key,
            filename: certificate.filename,
            mime_type: certificate.mime_type,
            file_size: certificate.file_size,
          }
        : {}),
    update: (id: string, body: Partial<Pick<ComplianceItem,
      'title' | 'description' | 'category' | 'is_mandatory' | 'owner_user_id' | 'due_date' | 'reference' | 'expires_on'>> & {
      status?: 'not_started' | 'in_progress' | 'blocked';
    }) => patch<ComplianceItem>(`/compliance/${id}`, body),
    markNotApplicable: (id: string, reason: string) =>
      post<ComplianceItem>(`/compliance/${id}/not-applicable`, { reason }),
    /** `stage` applies one stage of the template only — "Apply completion checklist" sends 'completion'. */
    applyTemplate: (projectId: string, templateId?: string, stage?: 'pre_start' | 'completion') =>
      post<{ applied: number }>('/compliance/apply-template', { project_id: projectId, template_id: templateId, ...(stage ? { stage } : {}) }),
    blockers: (projectId: string) =>
      get<{ data: ComplianceBlocker[] }>(`/compliance/blockers?project_id=${projectId}`),
    overdue: () => get<{ data: OverdueComplianceItem[] }>('/compliance/overdue'),
  },

  complianceTemplates: {
    getDefault: () => get<ComplianceTemplate>('/compliance-templates/default'),
    addItem: (templateId: string, body: Partial<Omit<ComplianceTemplateItem, 'id' | 'template_id'>>) =>
      post<ComplianceTemplateItem>(`/compliance-templates/${templateId}/items`, body),
    updateItem: (itemId: string, body: Partial<Omit<ComplianceTemplateItem, 'id' | 'template_id'>>) =>
      patch<ComplianceTemplateItem>(`/compliance-templates/items/${itemId}`, body),
    removeItem: (itemId: string) => del(`/compliance-templates/items/${itemId}`),
  },

  // Cost entries (builder-only — never exposed to clients per INV-009)
  costEntries: {
    list: (params?: { project_id?: string; phase_id?: string }) => {
      const qs = toQueryString(params);
      return get<{ data: unknown[]; total: number }>(`/cost-entries${qs ? `?${qs}` : ''}`);
    },
    get: (id: string) => get<CostEntry>(`/cost-entries/${id}`),
    // A task_id also places the entry on that task's phase — the API takes the phase from it.
    create: (body: {
      project_id: string; phase_id?: string; task_id?: string; commitment_id?: string;
      cost_type: CostType; category_label?: string;
      description: string; amount: number; incurred_at: string;
    }) => post('/cost-entries', body),
    update: (id: string, body: {
      task_id?: string | null; commitment_id?: string | null; cost_type?: CostType; category_label?: string;
      description?: string; amount?: number; incurred_at?: string;
    }) =>
      patch(`/cost-entries/${id}`, body),
    delete: (id: string) => del(`/cost-entries/${id}`),
  },

  // Company settings
  settings: {
    get: () => get<TenantSettings>('/settings'),
    update: (body: TenantSettingsUpdate) => patch<TenantSettings>('/settings', body),
    // The builder's own Stripe account (BC-232): where to send the builder_admin to connect it,
    // and its state read again on the way back.
    connectStripe: () => post<{ url: string }>('/settings/stripe/connect'),
    refreshStripe: () => post<{ stripe_account_status: StripeAccountStatus | null }>('/settings/stripe/refresh'),
  },

  // Comments (builder ↔ client threaded messages per entity)
  comments: {
    list: (entityType: string, entityId: string) =>
      get<{ data: Comment[] }>(`/comments?entity_type=${entityType}&entity_id=${entityId}`),
    // Every line comment on a quote in one request. Line comments are only ever read as a
    // set — nobody opens one line's thread — and the caller does not know which lines carry
    // one until it asks, so per-line lookups would mean a request per line of the quote.
    // Each row carries its `entity_id`, which is the line it belongs to.
    listForEstimateLines: (estimateId: string) =>
      get<{ data: Comment[] }>(`/comments?entity_type=estimate_line_item&estimate_id=${estimateId}`),
    create: (body: { project_id: string; entity_type: string; entity_id: string; body: string }) =>
      post<Comment>('/comments', body),
    delete: (id: string) => del(`/comments/${id}`),
  },

  projectUpdates: {
    list: (projectId: string) => get<{ data: ProjectUpdate[] }>(`/projects/${projectId}/updates`),
    get: (id: string) => get<ProjectUpdate>(`/project-updates/${id}`),
    generate: (projectId: string, body: { period_from?: string; period_to?: string } = {}) =>
      post<ProjectUpdateDraftResult>(`/projects/${projectId}/updates/generate`, body),
    createBlank: (projectId: string) => post<ProjectUpdate>(`/projects/${projectId}/updates`),
    save: (id: string, body: ProjectUpdateSaveBody) => patch<ProjectUpdate>(`/project-updates/${id}`, body),
    regenerate: (id: string) => post<ProjectUpdateDraftResult>(`/project-updates/${id}/regenerate`),
    send: (id: string) => post<ProjectUpdate>(`/project-updates/${id}/send`),
    discard: (id: string) => del(`/project-updates/${id}`),
  },

  // Superadmin
  admin: {
    stats: () => get<{ active_tenants: number; total_users: number; total_projects: number; open_invoices: number }>('/admin/stats'),
    emailHealth: () => get<{ ok: boolean | null; from: string; domain: string; problem?: string }>('/admin/health/email'),
    tenants: () => get<{ data: { id: string; company_name: string; plan: string; status: string; user_count: number; project_count: number; created_at: string }[] }>('/admin/tenants'),
    tenant: (id: string) => get<AdminTenantDetail>(`/admin/tenants/${id}`),
    createTenant: (body: { company_name: string; plan: string; admin_email: string; admin_full_name: string; admin_password: string }) =>
      post<{ tenant: { id: string; company_name: string; plan: string; status: string }; admin_email: string }>('/admin/tenants', body),
    updateTenant: (id: string, body: { plan?: string; status?: string; company_name?: string }) =>
      patch<{ id: string; company_name: string; plan: string; status: string }>(`/admin/tenants/${id}`, body),
    suspendTenant: (id: string) => del(`/admin/tenants/${id}`),
    users: () => get<{ data: { id: string; email: string; full_name: string; role: string; created_at: string; tenant_id: string; tenant_name: string; tenant_plan: string; tenant_status: string }[] }>('/admin/users'),
    user: (id: string) => get<unknown>(`/admin/users/${id}`),
    userAudit: (id: string) => get<{ data: unknown[]; total: number; limit: number }>(`/admin/users/${id}/audit-log`),
  },
};
